// Curated stock imagery — one distinct premium image per insight topic.
// All hosted on Unsplash with consistent crop / aspect for the cards.
const IMG = (id, q = "modern editorial") =>
  `https://images.unsplash.com/${id}?w=1600&q=80&auto=format&fit=crop&ixlib=rb-4.0.3`;

export const insights = [
  {
    slug: "rera-disputes-buyers-developers",
    category: "Real Estate & RERA",
    title: "RERA Disputes: What Homebuyers and Developers Should Understand",
    summary:
      "A practical overview of delayed possession, refund claims, compensation, documentation and remedies available under RERA.",
    image: IMG("photo-1545324418-cc1a3fa10c00"), // modern apartment building / urban real estate
    imageAlt: "Modern residential apartment building representing real estate and RERA matters",
    body: [
      "RERA proceedings are document-driven. The strength of a homebuyer’s claim or a developer’s defence often depends on what the agreement says, what was promised in writing and what the project records show.",
      "Delayed possession, defective construction, refund claims and compensation matters require a careful read of the builder-buyer agreement, payment timeline, possession date and project status. A focused legal notice and structured complaint are usually more useful than emotional escalation.",
      "Developers also need legal preparedness. Compliance, communication and contemporaneous record-keeping decide how a complaint is defended before the authority.",
    ],
  },
  {
    slug: "contract-drafting-prevents-disputes",
    category: "Contracts & Documentation",
    title: "Why Strong Contract Drafting Can Prevent Future Disputes",
    summary:
      "How clear clauses, payment terms, obligations and dispute resolution mechanisms can protect businesses and individuals.",
    image: IMG("photo-1450101499163-c8848c66ca85"), // pen on signed legal document
    imageAlt: "Pen resting on a signed legal document representing contract drafting",
    body: [
      "A contract is not a formality. It is the document that decides who is responsible, who must pay, what must be delivered and what happens when things go wrong.",
      "Founder disputes, vendor disagreements and recovery matters frequently trace back to vague payment clauses, weak termination terms or absent dispute resolution mechanisms. Clear drafting reduces the size of the disagreement long before a court is involved.",
      "Strong drafting is not about long contracts. It is about specific, enforceable and commercially aligned language.",
    ],
  },
  {
    slug: "civil-vs-commercial-litigation",
    category: "Litigation",
    title: "Civil Litigation vs Commercial Litigation: Key Differences Explained",
    summary:
      "A simple guide to understanding how civil and commercial disputes differ in nature, process and legal strategy.",
    image: IMG("photo-1505664194779-8beaceb93744"), // legal books / case files
    imageAlt: "Stacked legal books and case files representing civil and commercial litigation",
    body: [
      "Civil litigation covers a broad range of private disputes including property, recovery, family arrangements and contractual claims. Commercial litigation is a specialised stream that deals with disputes of a defined commercial nature and value.",
      "The choice of forum, applicable procedure and even the timeline expectations can differ. The legal strategy must be aligned to the nature of the dispute, not assumed.",
      "Before filing, it is worth asking: what is the relief sought, which forum will hear it most efficiently and what does the documentation actually support.",
    ],
  },
  {
    slug: "first-draft-legal-notice",
    category: "Legal Strategy",
    title: "Legal Notices: Why the First Draft Can Change the Direction of a Matter",
    summary:
      "Why a legal notice should be precise, factual and strategically drafted before any dispute escalates.",
    image: IMG("photo-1554224155-6726b3ff858f"), // legal documents close-up
    imageAlt: "Hand reviewing a formal legal notice document",
    body: [
      "A well-drafted notice can open the door to settlement. A weak one can hurt the case before it begins.",
      "Notices should record facts cleanly, identify the legal grounds, state the relief sought and avoid claims that cannot later be supported with documents.",
      "Equally important is replying to a notice with care. Silence and casual responses can be used against the client in subsequent proceedings.",
    ],
  },
  {
    slug: "due-diligence-property-checklist",
    category: "Real Estate & RERA",
    title: "Due Diligence Before Property Transactions: A Practical Checklist",
    summary:
      "Important legal checks before buying, investing in or dealing with property in Gurugram and Delhi NCR.",
    image: IMG("photo-1554224154-26032ffc0d07"), // person reviewing paperwork
    imageAlt: "Professional reviewing property paperwork during due diligence",
    body: [
      "Property due diligence is not a checklist exercise. It is a risk review. The purpose is to know what you are actually buying and what could go wrong later.",
      "Begin with title and ownership records. Move to encumbrances, approvals, mutation status, possession, agreement structure and pending litigation. Each layer can change the value or risk of the deal.",
      "A clean transaction depends as much on what is verified as on what is paid.",
    ],
  },
  {
    slug: "review-before-commercial-agreement",
    category: "Contracts & Documentation",
    title: "What Businesses Should Review Before Signing a Commercial Agreement",
    summary:
      "Key clauses every business should review before entering vendor, service, partnership or client agreements.",
    image: IMG("photo-1573164713988-8665fc963095"), // corporate boardroom / business meeting
    imageAlt: "Business professionals reviewing a commercial agreement at a meeting table",
    body: [
      "Most commercial agreements look standard until something goes wrong. The clauses that decide the outcome are the ones most often skimmed: scope, payment, termination, indemnity, limitation of liability and dispute resolution.",
      "Before signing, businesses should be clear on what is being delivered, what triggers payment, what happens on delay, who carries risk for third-party claims and where disputes will be heard.",
      "A short pre-signing review is always cheaper than a post-dispute reconstruction.",
    ],
  },
  {
    slug: "bail-and-anticipatory-bail",
    category: "Litigation",
    title: "Understanding Bail and Anticipatory Bail in Criminal Matters",
    summary:
      "A basic explainer on bail, anticipatory bail and why timely legal advice matters in criminal proceedings.",
    image: IMG("photo-1589994965851-a8f479c573a9"), // courthouse columns / dignified architecture
    imageAlt: "Courthouse architectural columns representing the criminal justice system",
    body: [
      "Bail is the legal release of a person from custody, usually on conditions. Anticipatory bail is sought in advance, where there is a reasonable apprehension of arrest in a non-bailable offence.",
      "The factors generally examined include the nature of the allegation, available evidence, role of the person, possibility of influencing witnesses and risk of absconding. Each matter turns on its own facts.",
      "In criminal proceedings, time is critical. Early legal advice helps preserve rights, prepare a clear factual record and approach the appropriate forum without delay.",
    ],
  },
  {
    slug: "startup-founder-contract-disputes",
    category: "Corporate Advisory",
    title: "How Start-ups Can Avoid Founder and Contract Disputes",
    summary:
      "Common legal gaps in start-ups and how strong documentation can prevent future conflict.",
    image: IMG("photo-1522071820081-009f0129c71c"), // startup team collaboration
    imageAlt: "Start-up founders collaborating in a modern office",
    body: [
      "Start-ups move quickly. Documentation often follows much later. By the time it does, expectations between founders, early hires, vendors and investors may have already diverged.",
      "A clear founder agreement, basic vendor and client contracts, NDAs, employment or consultant terms and an aligned understanding of equity, roles and exit can prevent most early-stage legal conflict.",
      "The right time to write things down is not when things go wrong. It is when things are still going well.",
    ],
  },
  {
    slug: "property-dispute-documents",
    category: "Real Estate & RERA",
    title: "Property Disputes in India: Documents That Can Strengthen Your Case",
    summary:
      "A practical look at title papers, agreements, payment records, possession documents and communication trails.",
    image: IMG("photo-1560518883-ce09059eeffa"), // residential house — property records context
    imageAlt: "Residential property representing ownership and title documentation",
    body: [
      "Property disputes are document-led. The party with a clearer paper trail is usually the better placed party — regardless of who feels they are right.",
      "Title documents, sale deeds, allotment letters, payment receipts, possession letters, mutation records, encumbrance certificates, builder communication and email trails together build the picture a court or authority will rely on.",
      "Before filing or replying, organise the documentation by date, source and relevance. The case strategy almost always follows the strength of the file.",
    ],
  },
  {
    slug: "negotiation-vs-litigation",
    category: "Legal Strategy",
    title: "Dispute Resolution: When Negotiation Works Better Than Litigation",
    summary:
      "Why some matters should be approached through negotiation, settlement or mediation before court proceedings.",
    image: IMG("photo-1556761175-b413da4baf72"), // handshake / formal agreement
    imageAlt: "Formal handshake at a negotiation table representing dispute resolution",
    body: [
      "Not every dispute should immediately become a long court battle. Some are best handled through a structured legal notice. Others can be closed through negotiation, settlement or mediation.",
      "The right path depends on what the client wants, how strong the documentation is, the cost-benefit of litigation, the time horizon and the practical relationship between the parties.",
      "Litigation is a tool, not a default. Choosing it consciously, rather than reactively, almost always leads to a better outcome.",
    ],
  },
];

export const getInsightBySlug = (slug) => insights.find((i) => i.slug === slug);
